// Cookie consent store. Reject-by-default; explicit opt-in for analytics +
// marketing categories. Choice persists in localStorage with a version stamp
// so we can re-prompt cleanly when categories change (e.g. when we add a new
// vendor that needs its own bucket).
//
// Scripts/SDKs that need consent should read `getCookieConsent()` OR subscribe
// to changes via `onCookieConsentChange` before loading.

const STORAGE_KEY = "fh.cookieConsent";
// Bump when we add/remove a category or materially change wording so users
// see the banner again.
export const COOKIE_CONSENT_VERSION = "2026-05-21-v1";

export type CookieCategory = "necessary" | "analytics" | "marketing";

export interface CookieConsent {
  version: string;
  decidedAt: string;       // ISO timestamp
  necessary: true;         // always true — included for shape consistency
  analytics: boolean;
  marketing: boolean;
}

type Listener = (c: CookieConsent | null) => void;
const listeners = new Set<Listener>();

export function getCookieConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsent;
    if (parsed.version !== COOKIE_CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setCookieConsent(c: Omit<CookieConsent, "version" | "decidedAt" | "necessary">) {
  const stored: CookieConsent = {
    version: COOKIE_CONSENT_VERSION,
    decidedAt: new Date().toISOString(),
    necessary: true,
    analytics: c.analytics,
    marketing: c.marketing,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  listeners.forEach((fn) => fn(stored));
  return stored;
}

export function clearCookieConsent() {
  localStorage.removeItem(STORAGE_KEY);
  listeners.forEach((fn) => fn(null));
}

export function onCookieConsentChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function hasConsent(category: CookieCategory): boolean {
  if (category === "necessary") return true;
  const c = getCookieConsent();
  return c?.[category] === true;
}
